// src/components/QuoteForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import TagInput from './TagInput';
import { 
  Brain, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2
} from 'lucide-react';

const QuoteForm = ({ 
  initialQuote = {}, 
  onSubmit, 
  existingTags = [], 
  isEditing = false,
  onCancel = null 
}) => {
  // Set up default values
  const defaultQuote = {
    claim_text: '',
    claim_source: '',
    // Format date as MM-DD-YYYY
    claim_date: new Date().toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: 'numeric'}).replace(/\//g, '-'),
    reality_text: '',
    reality_sources: '[]',
    veracity_rating: '',
    citation: '',
    video_url: '',
    claim_context: '',
    tags: ''
  };

  // Merge provided initialQuote with defaults
  const [quote, setQuote] = useState({
    ...defaultQuote,
    ...initialQuote
  });
  
  // State for Claude analysis
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [frameworkData, setFrameworkData] = useState({
    pillars: [],
    tactics: [],
    precedents: []
  });

  // Source editing state for reality sources
  const [sources, setSources] = useState([]);
  const [currentSource, setCurrentSource] = useState({ name: '', description: '', url: '' });
  const [editingSourceIndex, setEditingSourceIndex] = useState(null);

  // Initialize sources from reality_sources JSON
  useEffect(() => {
    try {
      const parsedSources = typeof quote.reality_sources === 'string' ? 
        JSON.parse(quote.reality_sources) : 
        (Array.isArray(quote.reality_sources) ? quote.reality_sources : []);
      setSources(parsedSources);
    } catch (e) {
      console.error('Error parsing reality sources:', e);
      setSources([]);
    }
  }, [quote.reality_sources]);
  
  // Clear form data on unmount to prevent persistence issues
  useEffect(() => {
    return () => {
      try {
        localStorage.removeItem('quote_form_backup');
        sessionStorage.removeItem('lastClaudeAnalysis');
      } catch (e) {
        console.warn("Failed to clear form backup on unmount:", e);
      }
    };
  }, []);

  // Veracity rating options
  const veracityOptions = [
    { value: '', label: 'Select a rating' },
    { value: 'False', label: 'False' },
    { value: 'Misleading', label: 'Misleading' },
    { value: 'Partly True', label: 'Partly True' },
    { value: 'Mostly True', label: 'Mostly True' },
    { value: 'True', label: 'True' }
  ];

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuote(prev => ({ ...prev, [name]: value }));
  };

  // Handle source form input changes
  const handleSourceChange = (e) => {
    const { name, value } = e.target;
    setCurrentSource(prev => ({ ...prev, [name]: value }));
  };

  // Add a source to reality_sources
  const addSource = (e) => {
    e.preventDefault();
    if (!currentSource.name) return;

    const newSources = [...sources];
    
    if (editingSourceIndex !== null) {
      // Edit existing source
      newSources[editingSourceIndex] = currentSource;
    } else {
      // Add new source
      newSources.push(currentSource);
    }
    
    setSources(newSources);
    setQuote(prev => ({ ...prev, reality_sources: JSON.stringify(newSources) }));
    setCurrentSource({ name: '', description: '', url: '' });
    setEditingSourceIndex(null);
  };

  // Edit a source
  const editSource = (index) => {
    setCurrentSource(sources[index]);
    setEditingSourceIndex(index);
  };

  // Remove a source
  const removeSource = (index) => {
    const newSources = sources.filter((_, i) => i !== index);
    setSources(newSources);
    setQuote(prev => ({ ...prev, reality_sources: JSON.stringify(newSources) }));
  };

  // State for notifications
  const [notification, setNotification] = useState({ type: '', message: '', visible: false });
  
  // Show notification instead of alert
  const showNotification = useCallback((type, message) => {
    setNotification({ type, message, visible: true });
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 5000);
  }, []);
  
  // Analyze with Claude AI
  const analyzeWithClaude = async () => {
    if (!quote.claim_text || !quote.claim_source) {
      setAnalyzeError('Please enter at least the claim text and source before analyzing');
      return;
    }
    
    setAnalyzing(true);
    setAnalyzeError(null);
    
    try {
      // Save current form state to localStorage in case user navigates away
      try {
        localStorage.setItem('quote_form_backup', JSON.stringify({
          quote,
          sources,
          frameworkData
        }));
      } catch (e) {
        console.warn('Failed to save form backup:', e);
      }
      
      // Prepare all available data for analysis
      const quoteData = {
        ...quote,
        // Format tags for analysis
        tags: quote.tags
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag !== ''),
        // Ensure claim_context is included if provided
        claim_context: quote.claim_context || ''
      };
      
      const response = await fetch('/api/claude-analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ quoteData })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }
      
      const analysisData = await response.json();
      
      // Use analysis to enhance quote data
      if (analysisData) {
        const updates = {};
        
        // Update reality text if it's empty or minimal
        if ((!quote.reality_text || quote.reality_text.length < 50) && analysisData.reality_text) {
          updates.reality_text = analysisData.reality_text;
        }
        
        // Update veracity rating if not selected
        if (analysisData.veracity_rating && (!quote.veracity_rating || quote.veracity_rating === '')) {
          updates.veracity_rating = analysisData.veracity_rating;
        }
        
        // Update claim date if provided by Claude
        if (analysisData.claim_date && analysisData.claim_date.match(/^\d{2}-\d{2}-\d{4}$/)) {
          console.log('Using claim date from Claude analysis:', analysisData.claim_date);
          updates.claim_date = analysisData.claim_date;
        }
        
        // Add sources if available
        if (analysisData.sources && analysisData.sources.length > 0) {
          // Parse current sources if any
          let currentSources = [];
          try {
            currentSources = JSON.parse(quote.reality_sources || '[]');
          } catch (e) {
            console.warn('Error parsing existing sources:', e);
            currentSources = [];
          }
          
          // Only add new sources if we don't already have any
          if (currentSources.length === 0) {
            updates.reality_sources = JSON.stringify(analysisData.sources);
            // Update sources state to render the source list
            setSources(analysisData.sources);
          }
        }
        
        // Add suggested tags
        if (analysisData.suggested_tags && analysisData.suggested_tags.length > 0) {
          const currentTags = quote.tags
            .split(',')
            .map(tag => tag.trim().toLowerCase()) // Convert current tags to lowercase
            .filter(tag => tag !== '');
          
          // Get existing tags from the existingTags prop (from database)
          const existingTagsLower = existingTags.map(tag => tag.toLowerCase());
          
          // First, prefer tags that already exist in the database
          const suggestedTagsWithExisting = analysisData.suggested_tags.map(tag => {
            const tagLower = tag.toLowerCase();
            // If we have an existing tag with the same name (case-insensitive), use that one
            const existingMatch = existingTags.find(existing => existing.toLowerCase() === tagLower);
            return existingMatch || tagLower; // Use existing match if found, otherwise use lowercase tag
          });
          
          // Add new tags that aren't already in the list, keeping everything lowercase
          const newTags = [...new Set([...currentTags, ...suggestedTagsWithExisting])];
          updates.tags = newTags.join(',');
        }
        
        // Apply all updates at once
        if (Object.keys(updates).length > 0) {
          setQuote(prev => ({ ...prev, ...updates }));
          
          // Store analysis data for framework elements in component state
          // This will allow it to be submitted with the form
          let hasFrameworkData = false;
          
          if (analysisData.pillars && analysisData.pillars.length > 0) {
            setFrameworkData(prev => ({
              ...prev,
              pillars: analysisData.pillars
            }));
            hasFrameworkData = true;
          }
          
          if (analysisData.tactics && analysisData.tactics.length > 0) {
            setFrameworkData(prev => ({
              ...prev,
              tactics: analysisData.tactics
            }));
            hasFrameworkData = true;
          }
          
          if (analysisData.precedents && analysisData.precedents.length > 0) {
            setFrameworkData(prev => ({
              ...prev,
              precedents: analysisData.precedents
            }));
            hasFrameworkData = true;
          }
          
          // Save updated state to localStorage
          try {
            localStorage.setItem('quote_form_backup', JSON.stringify({
              quote: { ...quote, ...updates },
              sources: analysisData.sources && analysisData.sources.length > 0 ? analysisData.sources : sources,
              frameworkData: {
                pillars: analysisData.pillars || [],
                tactics: analysisData.tactics || [],
                precedents: analysisData.precedents || []
              }
            }));
          } catch (e) {
            console.warn('Failed to save updated form state:', e);
          }
          
          // Also store in sessionStorage as fallback
          try {
            sessionStorage.setItem('lastClaudeAnalysis', JSON.stringify(analysisData));
          } catch (e) {
            console.warn('Could not save analysis to session storage:', e);
          }
          
          // Show success notification
          showNotification(
            'success', 
            hasFrameworkData 
              ? "Analysis complete! The form has been updated with Claude's fact-check results and framework connections."
              : "Analysis complete! The form has been updated with Claude's fact-check results."
          );
        } else {
          showNotification('warning', "Analysis complete, but no new information was added to the form.");
        }
      }
    } catch (error) {
      console.error('Error analyzing with Claude:', error);
      setAnalyzeError(error.message || 'Failed to analyze with Claude');
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Load form state from localStorage on component mount (only for new quotes)
  useEffect(() => {
    // Only attempt to restore state if we're creating a new quote (not editing)
    if (isEditing) {
      return;
    }
    
    try {
      const savedState = localStorage.getItem('quote_form_backup');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        
        // Only restore if there's meaningful data
        if (parsedState) {
          if (parsedState.quote && Object.keys(parsedState.quote).length > 0) {
            // Only restore if current form is empty
            if (!quote.claim_text || quote.claim_text === '') {
              setQuote(parsedState.quote);
            }
          }
          
          if (parsedState.sources && parsedState.sources.length > 0) {
            setSources(parsedState.sources);
          }
          
          if (parsedState.frameworkData) {
            setFrameworkData(parsedState.frameworkData);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to restore form state:', e);
    }
  }, [isEditing]);

  // Form submission
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (submitting) {
      return;
    }
    
    // Set submitting state
    setSubmitting(true);
    
    // Parse sources if needed to fix any issues
    let parsedSources = [];
    try {
      // If reality_sources is a string, parse it to ensure it's valid before sending
      if (typeof quote.reality_sources === 'string' && quote.reality_sources) {
        parsedSources = JSON.parse(quote.reality_sources);
        
        // Validate that each source has a name to prevent validation errors
        if (Array.isArray(parsedSources)) {
          parsedSources = parsedSources.map(source => ({
            name: source.name || 'Unnamed Source',
            description: source.description || '',
            url: source.url || ''
          }));
        }
      }
    } catch (e) {
      console.warn('Error parsing reality_sources:', e);
      // Use the sources state array as a fallback
      parsedSources = sources;
    }
    
    // Prepare submission data
    const formData = {
      ...quote,
      // Use the validated sources
      reality_sources: parsedSources,
      // Parse tags into array
      tags: quote.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag !== ''),
      // Ensure video_url is correctly passed
      video_url: quote.video_url || '',
      // Ensure claim_context is correctly passed
      claim_context: quote.claim_context || '',
      // Include the framework analysis data from Claude
      // Only include if there's actually data present
      ...(frameworkData && 
         (frameworkData.pillars?.length > 0 || 
          frameworkData.tactics?.length > 0 || 
          frameworkData.precedents?.length > 0) ? 
         { frameworkData } : {}),
      // Set published to true by default so quotes appear immediately
      published: true
    };
    
    console.log('Submitting quote data with framework elements:', formData);
    
    try {
      await onSubmit(formData);
      // Show success notification
      showNotification('success', 'Quote submitted successfully!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      showNotification('warning', `Error submitting quote: ${error.message}`);
    } finally {
      // Reset submitting state
      setSubmitting(false);
    }
  };

  // Preview framework data
  const FrameworkPreview = () => {
    // Only show if we have framework data
    if (!frameworkData.pillars.length && !frameworkData.tactics.length && !frameworkData.precedents.length) {
      return null;
    }
    
    return (
      <div className="bg-purple-50 p-4 rounded-md border border-purple-200 mb-6">
        <div className="flex items-center mb-3">
          <Brain className="h-5 w-5 text-purple-700 mr-2" />
          <h3 className="font-medium text-purple-800">Claude's Framework Analysis Preview</h3>
        </div>
        
        <div className="space-y-4">
          {/* Pillars */}
          {frameworkData.pillars.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Democratic Pillars:</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto bg-white rounded p-2 border border-gray-200">
                {frameworkData.pillars.map(pillar => (
                  <div key={pillar.id} className="text-sm p-2 border-b border-gray-100">
                    <div className="flex justify-between">
                      <span className="font-medium">{pillar.name || `Pillar ${pillar.id}`}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        pillar.impact_level === 'severe' ? 'bg-red-100 text-red-800' :
                        pillar.impact_level === 'moderate' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {pillar.impact_level} impact
                      </span>
                    </div>
                    <p className="text-gray-600 mt-1">{pillar.justification}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Tactics */}
          {frameworkData.tactics.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Authoritarian Tactics:</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto bg-white rounded p-2 border border-gray-200">
                {frameworkData.tactics.map(tactic => (
                  <div key={tactic.id} className="text-sm p-2 border-b border-gray-100">
                    <div className="font-medium">{tactic.name || `Tactic ${tactic.id}`}</div>
                    <p className="text-gray-600 mt-1">{tactic.justification}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Precedents */}
          {frameworkData.precedents.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Historical Precedents:</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto bg-white rounded p-2 border border-gray-200">
                {frameworkData.precedents.map(precedent => (
                  <div key={precedent.id} className="text-sm p-2 border-b border-gray-100">
                    <div className="font-medium">
                      {precedent.country || `Precedent ${precedent.id}`} 
                      {precedent.time_period && ` (${precedent.time_period})`}
                    </div>
                    <p className="text-gray-600 mt-1">{precedent.relevance_note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-2 text-sm text-gray-700">
            <p>These framework connections will be added when you submit the form.</p>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {analyzeError && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{analyzeError}</span>
          </div>
        </div>
      )}
      
      {/* Show Framework Preview after Claude Analysis */}
      <FrameworkPreview />

      <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mb-6">
        <h3 className="text-blue-800 font-medium mb-2">Claim Information</h3>
        
        <div className="mb-4">
          <label htmlFor="claim_text" className="block text-sm font-medium text-gray-700 mb-1">
            Claim Text
          </label>
          <textarea
            id="claim_text"
            name="claim_text"
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.claim_text}
            onChange={handleChange}
            required
          ></textarea>
          <p className="mt-1 text-sm text-gray-500">
            The exact claim being fact-checked, typically a direct quote.
          </p>
        </div>
        
        <div className="mb-4">
          <label htmlFor="claim_context" className="block text-sm font-medium text-gray-700 mb-1">
            Context for Claude (optional)
          </label>
          <textarea
            id="claim_context"
            name="claim_context"
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.claim_context || ''}
            onChange={handleChange}
            placeholder="Add additional context about this claim to help Claude's analysis (e.g., article text, background information)"
          ></textarea>
          <p className="mt-1 text-sm text-gray-500">
            Additional context that will be provided to Claude during analysis but won't be displayed publicly. Useful for current events or complex topics.
          </p>
        </div>
        
        <div className="mb-4">
          <label htmlFor="claim_source" className="block text-sm font-medium text-gray-700 mb-1">
            Claim Source
          </label>
          <input
            type="text"
            id="claim_source"
            name="claim_source"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.claim_source}
            onChange={handleChange}
            placeholder="E.g., Senator Smith at Press Conference"
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            Who made the claim and in what context.
          </p>
        </div>
        
        <div className="mb-4">
          <label htmlFor="claim_date" className="block text-sm font-medium text-gray-700 mb-1">
            Claim Date
          </label>
          <input
            type="date"
            id="claim_date"
            name="claim_date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.claim_date}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="citation" className="block text-sm font-medium text-gray-700 mb-1">
            Citation URL (optional)
          </label>
          <input
            type="url"
            id="citation"
            name="citation"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.citation || ''}
            onChange={handleChange}
            placeholder="https://example.com/source-of-claim"
          />
          <p className="mt-1 text-sm text-gray-500">
            Link to where the claim was published or recorded.
          </p>
        </div>
        
        <div className="mb-4">
          <label htmlFor="veracity_rating" className="block text-sm font-medium text-gray-700 mb-1">
            Veracity Rating
          </label>
          <select
            id="veracity_rating"
            name="veracity_rating"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.veracity_rating}
            onChange={handleChange}
            required
          >
            {veracityOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="bg-green-50 p-4 rounded-md border border-green-200 mb-6">
        <h3 className="text-green-800 font-medium mb-2">Reality Information</h3>
        
        <div className="mb-4">
          <label htmlFor="reality_text" className="block text-sm font-medium text-gray-700 mb-1">
            Reality Text
          </label>
          <textarea
            id="reality_text"
            name="reality_text"
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.reality_text}
            onChange={handleChange}
            required
          ></textarea>
          <p className="mt-1 text-sm text-gray-500">
            The factual information that corrects or contextualizes the claim.
          </p>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reality Sources
          </label>
          
          {/* Source list */}
          {sources.length > 0 && (
            <div className="mb-4 space-y-2">
              {sources.map((source, index) => (
                <div key={index} className="flex items-center p-2 border rounded bg-white">
                  <div className="flex-grow">
                    <p className="font-medium">{source.name}</p>
                    {source.description && <p className="text-sm text-gray-600">{source.description}</p>}
                    {source.url && (
                      <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                        {source.url}
                      </a>
                    )}
                  </div>
                  <div className="flex-shrink-0 space-x-2">
                    <button 
                      type="button"
                      onClick={() => editSource(index)} 
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button 
                      type="button"
                      onClick={() => removeSource(index)} 
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Source form */}
          <div className="bg-white p-3 border rounded-md">
            <h4 className="text-sm font-medium mb-2">
              {editingSourceIndex !== null ? 'Edit Source' : 'Add Source'}
            </h4>
            <div className="space-y-3">
              <div>
                <label htmlFor="source_name" className="block text-xs font-medium text-gray-700">
                  Source Name*
                </label>
                <input
                  type="text"
                  id="source_name"
                  name="name"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={currentSource.name}
                  onChange={handleSourceChange}
                  placeholder="E.g., Congressional Budget Office Report"
                  // Don't require name when displaying sources
                  // The form submission will provide a default if missing
                />
              </div>
              
              <div>
                <label htmlFor="source_description" className="block text-xs font-medium text-gray-700">
                  Description
                </label>
                <input
                  type="text"
                  id="source_description"
                  name="description"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={currentSource.description}
                  onChange={handleSourceChange}
                  placeholder="E.g., Analysis of Federal Deficits FY 2021-2023"
                />
              </div>
              
              <div>
                <label htmlFor="source_url" className="block text-xs font-medium text-gray-700">
                  URL
                </label>
                <input
                  type="url"
                  id="source_url"
                  name="url"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={currentSource.url}
                  onChange={handleSourceChange}
                  placeholder="https://example.com/report"
                />
              </div>
              
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={addSource}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                >
                  {editingSourceIndex !== null ? 'Update Source' : 'Add Source'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-6">
        <h3 className="text-gray-800 font-medium mb-2">Additional Information</h3>
        
        <div className="mb-4">
          <label htmlFor="video_url" className="block text-sm font-medium text-gray-700 mb-1">
            Video URL (optional)
          </label>
          <input
            type="url"
            id="video_url"
            name="video_url"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={quote.video_url || ''}
            onChange={handleChange}
            placeholder="https://youtube.com/watch?v=example"
          />
          <p className="mt-1 text-sm text-gray-500">
            Link to a related video (YouTube URLs will be embedded).
          </p>
        </div>
        
        <div className="mb-4">
          <label htmlFor="quoteTags" className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          <TagInput
            initialTags={typeof quote.tags === 'string' ? quote.tags : (Array.isArray(quote.tags) ? quote.tags.join(',') : '')}
            onChange={(tags) => setQuote({...quote, tags: tags})}
            existingTags={existingTags}
          />
          <p className="mt-1 text-sm text-gray-500">
            Type to create new tags or select from existing ones. Press Enter or comma to add multiple tags.
          </p>
        </div>
      </div>
      
      {/* Notification Toast */}
      {notification.visible && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-md shadow-lg ${
          notification.type === 'success' ? 'bg-green-50 border border-green-200' :
          notification.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center">
            {notification.type === 'success' && <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />}
            {notification.type === 'warning' && <AlertTriangle className="h-5 w-5 mr-2 text-yellow-500" />}
            <p className={`text-sm ${
              notification.type === 'success' ? 'text-green-800' :
              notification.type === 'warning' ? 'text-yellow-800' :
              'text-blue-800'
            }`}>{notification.message}</p>
          </div>
        </div>
      )}
      
      <div className="flex justify-between pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
        )}
        
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={analyzeWithClaude}
            disabled={analyzing}
            className="flex items-center px-4 py-2 bg-purple-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Brain className="h-4 w-4 mr-2" />
                Analyze with Claude
              </>
            )}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Saving...'}
              </span>
            ) : (
              isEditing ? 'Update Claim' : 'Save Claim'
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default QuoteForm;